// ./src/utils/api/MarketingReportService.ts
//
// The marketing report (ROADMAP A2b #59) — visits, unique visitors and conversions per marketing
// channel, plus the Direct / untagged row. OpResult-native, read-only.
//
// ⚠ Spec: eventsmagic-api/docs/MARKETING-ATTRIBUTION.md. Two things from it decide how this data
// must be RENDERED, and both are easy to throw away in a table cell:
//   • `trackedFrom` non-null means "we were not counting for part of this window" — the row's
//     zeroes are an UNKNOWN, not a result, and the UI must say so rather than print a confident 0.
//   • `conversionRate` is null (never 0) when nobody has visited yet. "0% converted" and "nobody
//     has been here" are different sentences and only one of them is a problem.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

// =============================================================================
// GraphQL
// =============================================================================

const MARKETING_REPORT_ROW_FIELDS = `
  marketingChannelId
  name
  code
  isDirect
  visits
  uniqueVisitors
  newVisitors
  rsvps
  orders
  revenue
  currencyCode
  conversionRate
  trackedFrom
  mixedCurrency
`;

const MARKETING_REPORT_QUERY = gql`
  query MarketingReport($params: MarketingReportInput) {
    marketingReport(params: $params) {
      code
      data {
        rows { ${MARKETING_REPORT_ROW_FIELDS} }
        totals { ${MARKETING_REPORT_ROW_FIELDS} }
        unmatchedCodes { channelCode visits }
        from
        to
        showsRsvps
        showsOrders
        trackingStartedAt
        attributionModel
      }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api MarketingReport schema)
// =============================================================================

export interface MarketingReportRow {
  /** Null on the Direct / untagged row. */
  marketingChannelId?: string | null;
  name?: string | null;
  code?: string | null;
  isDirect?: boolean | null;
  visits?: number | null;
  uniqueVisitors?: number | null;
  newVisitors?: number | null;
  rsvps?: number | null;
  orders?: number | null;
  revenue?: number | null;
  currencyCode?: string | null;
  /**
   * Only ever true on `totals`: the rows span more than one currency, so there is no single
   * revenue figure. ⚠ The UI must DECLINE to print one rather than adding CAD to USD — the sum
   * would be money in no currency at all, under a single symbol, and nothing about it looks wrong.
   */
  mixedCurrency?: boolean | null;
  /** Percentage. NULL — not 0 — when nobody has visited yet. */
  conversionRate?: number | null;
  /** Non-null = this row's numbers do NOT cover the whole window; nothing was counted before this. */
  trackedFrom?: string | null;
}

export interface MarketingReportUnmatched {
  channelCode?: string | null;
  visits?: number | null;
}

export interface MarketingReport {
  rows?: MarketingReportRow[] | null;
  totals?: MarketingReportRow | null;
  unmatchedCodes?: MarketingReportUnmatched[] | null;
  from?: string | null;
  to?: string | null;
  /**
   * Whether the RSVPs column means anything in this scope — false when every event in scope is
   * ticketed. ⚠ The SERVER decides: the rule needs the event mode, which this app does not have on
   * the account-wide page, and both report pages must agree. ⚠ It is also true whenever RSVPs were
   * actually counted, so a column with data is never hidden.
   */
  showsRsvps?: boolean | null;
  /** Same, for Orders + Revenue: true when any event in scope is ticketed, or any order landed. */
  showsOrders?: boolean | null;
  trackingStartedAt?: string | null;
  /** Which model produced these numbers. Shown on screen so the figures can be argued with. */
  attributionModel?: string | null;
}

export interface MarketingReportInput {
  /** Scope to ONE event; omitted = the whole account. Also the crew authorization context. */
  eventId?: string | null;
  /** YYYY-MM-DD. Defaults to 30 days ago server-side. */
  from?: string | null;
  /** YYYY-MM-DD, inclusive. Defaults to today server-side. */
  to?: string | null;
}

// =============================================================================
// Service
// =============================================================================

export class MarketingReportService {
  /** The report for an account, or for one event. Always a fresh read — it is a dashboard. */
  static async get(params: MarketingReportInput = {}): Promise<OpResult> {
    return ApiService.executeQuery(MARKETING_REPORT_QUERY, { params }, { fetchPolicy: "network-only" });
  }
}
